import { createLogger } from "@/logger";

const log = createLogger("feed");

import { bootstrapWorkspaceFeedItemsPaginated, listFeedItemEventLog, type WsItem } from "@storyboard/web-client";
import { transport } from "@/config";
import { mergeContiguousRange, type PageAnchoring } from "@/models/feedContiguity";
import { bulkUpsertItemAndCalculateMembership } from "@/models/repositories/Item";
import { idx, rowsByIndex, store } from "@/store/store";
import type { Feed } from "@/store/types";

/**
 * Where a page request takes its pagination cursor from.
 *
 * - `tail` — no cursor, so the server returns the newest page. Used on cold open.
 * - `contiguity` — the matching bound of the feed's proven-contiguous span, so the page abuts
 *   history already trusted rather than whatever rows happen to be cached.
 * - `item` — a specific item id, for deeplinks. Deliberately disjoint from the trusted span.
 */
export type FeedFetchAnchor = "tail" | "contiguity" | "item";

/**
 * Fetches a page of feed items and folds it into the feed's local pagination state.
 *
 * The pagination cursor is resolved here rather than by the caller: reading it from the persisted
 * contiguity span is what keeps scroll-back from leaping over holes in the local cache (WEB-2197).
 *
 * The `tail` and `contiguity` anchors read the feed's slice of the item event log: `older` in history mode, newest
 * first, at or before the span's floor; `newer` in delta mode, after the span's ceiling. The event log has no item
 * anchor, so a deeplink still reads the paginated route.
 *
 * @param direction Whether the page runs forward (`newer`) or backward (`older`) from the cursor.
 * @param referenceItemId Required when `anchor` is `item`; ignored otherwise.
 * @returns The fetched items, and whether the page reached the end of the feed's history in its direction.
 */
export async function fetchFeedItems({
  workspaceId,
  feedId,
  direction,
  pageSize = 200,
  anchor = "tail",
  referenceItemId,
}: {
  workspaceId: string;
  feedId: string;
  direction: "newer" | "older";
  pageSize?: number;
  anchor?: FeedFetchAnchor;
  referenceItemId?: string;
}) {
  log.debug("Fetching feed items:", { workspaceId, feedId, direction, pageSize, anchor, referenceItemId });

  try {
    const byItemId = anchor === "item" && !!referenceItemId;
    if (anchor === "item" && !byItemId) {
      log.warn("Item-anchored fetch is missing its reference item; falling back to the newest page", { feedId });
    }
    const referenceTimestamp = byItemId
      ? undefined
      : resolveAnchorTimestamp(store.get("feed", feedId), direction, anchor);
    const unanchored = !byItemId && !referenceTimestamp;

    // A `newer` page can only extend a span that exists; without one there is no trusted ceiling to
    // walk forward from, and an unanchored ascending request would return the feed's oldest page.
    // Marked `skipped` so callers don't read the empty page as the end of history.
    if (anchor === "contiguity" && direction === "newer" && unanchored) {
      log.debug("Skipping newer fetch: feed has no contiguous span yet", { feedId });
      return { items: [], pageSize, lastPage: false, failed: false, skipped: true };
    }

    const { items, lastPage } = byItemId
      ? await itemAnchoredPage({ workspaceId, feedId, direction, pageSize, referenceItemId })
      : await eventLogPage({ workspaceId, feedId, direction, pageSize, referenceTimestamp });

    if (items.length > 0) {
      await bulkUpsertItemAndCalculateMembership(items);
    }

    // Re-read rather than reuse the row the anchor came from: the round-trip above is long enough
    // for another page to have landed, and folding this page into a stale span would drop its work.
    const currentFeed = store.get("feed", feedId);
    const updates: Partial<Feed> = {};

    // The server ran out of history in that direction.
    if (lastPage) {
      if (direction === "older" && !currentFeed?.loadedFirstPage) {
        updates.loadedFirstPage = true;
      }
      // Only an unanchored page proves the cache covers the live tail, and only durably enough to
      // let the next visit skip its cold-open fetch. Catching up to the tail mid-session is a
      // momentary fact that belongs in the caller's session-scoped ref, not in the store: a
      // feed flagged here is not re-fetched on reopen, so anything the realtime feed missed while
      // the channel was closed would never load.
      if (unanchored && !currentFeed?.loadedLastPage) {
        updates.loadedLastPage = true;
      }
    }

    // Deeplink pages are a deliberate island somewhere in history, so they never move the span.
    if (!byItemId) {
      Object.assign(updates, contiguitySpanUpdates(currentFeed, items, unanchored ? "tail" : "anchored"));
    }

    if (Object.keys(updates).length > 0) {
      store.update("feed", feedId, (row) => ({ ...row, ...updates }));
    }

    return { items, pageSize, lastPage, failed: false, skipped: false };
  } catch (error) {
    log.error("Error fetching feed items:", error);
    // Flag the failure so callers don't read an empty result as the end of history and disable
    // their own paging for the session over what may be a transient network blip.
    return { items: [], pageSize, lastPage: false, failed: true, skipped: false };
  }
}

/** One page of a feed's items, and whether it reached the end of the feed's history in its direction. */
type FeedPage = { items: WsItem[]; lastPage: boolean };

/**
 * One page of the feed's slice of the item event log.
 *
 * Without a bound, the feed's newest page in history order, whichever the direction: a caller that wants to page
 * forward needs a span to start from. `older` reads history mode at or before the span's floor; `newer` reads delta
 * mode after the span's ceiling. A page without a cursor is the last in its direction.
 */
async function eventLogPage({
  workspaceId,
  feedId,
  direction,
  pageSize,
  referenceTimestamp,
}: {
  workspaceId: string;
  feedId: string;
  direction: "newer" | "older";
  pageSize: number;
  referenceTimestamp: string | undefined;
}): Promise<FeedPage> {
  const bound =
    referenceTimestamp === undefined
      ? {}
      : direction === "older"
        ? { until: referenceTimestamp }
        : { since: referenceTimestamp };
  const page = await listFeedItemEventLog(transport, { workspaceId, feedIds: feedId, limit: pageSize, ...bound });
  return { items: page.items, lastPage: page.cursor === undefined };
}

/** One page either side of a deeplinked item, from the paginated route; a short page is the last. */
async function itemAnchoredPage({
  workspaceId,
  feedId,
  direction,
  pageSize,
  referenceItemId,
}: {
  workspaceId: string;
  feedId: string;
  direction: "newer" | "older";
  pageSize: number;
  referenceItemId: string | undefined;
}): Promise<FeedPage> {
  const result = await bootstrapWorkspaceFeedItemsPaginated(transport, {
    workspaceId,
    feedId,
    pageSize,
    sortAscending: direction === "newer",
    useCreatedAt: true,
    itemId: referenceItemId,
  });
  const items = result?.items ?? [];
  return { items, lastPage: items.length < pageSize };
}

/** What the store actually holds for a feed, read synchronously from the item index. */
export type LocalFeedCache = {
  /** Non-deleted local rows for the feed. */
  count: number;
  /** `createdAt` of the oldest local row — soft-deleted included — or null when none exist. */
  oldestCreatedAt: string | null;
  /** `createdAt` of the newest local row — soft-deleted included — or null when none exist. */
  newestCreatedAt: string | null;
};

/**
 * Summarizes the feed's local rows from the store index.
 *
 * The bounds include soft-deleted rows while the count excludes them: a deleted row still proves the cache holds its
 * span position (deletion is a rendering concern, and span bounds never move on a soft delete), but only renderable
 * rows can fill a render window.
 */
export function localFeedCache(feedId: string): LocalFeedCache {
  let count = 0;
  let oldestCreatedAt: string | null = null;
  let newestCreatedAt: string | null = null;
  for (const row of rowsByIndex("item", idx.itemsByFeed, feedId)) {
    if (row.deletedAt == null) count++;
    if (oldestCreatedAt === null || new Date(row.createdAt).getTime() < new Date(oldestCreatedAt).getTime()) {
      oldestCreatedAt = row.createdAt;
    }
    if (newestCreatedAt === null || new Date(row.createdAt).getTime() > new Date(newestCreatedAt).getTime()) {
      newestCreatedAt = row.createdAt;
    }
  }
  return { count, oldestCreatedAt, newestCreatedAt };
}

/**
 * Whether a cold open may skip its initial tail fetch and render from local rows alone.
 *
 * The persisted `loadedLastPage` flag makes that promise, but the cache can stop backing it after the flag was
 * written — feed eviction prunes rows, and a failed snapshot persist drops them (WEB-2254) — so the flag is honored
 * only when the store corroborates it:
 *
 * - Rows actually exist. An empty store can never corroborate: a genuinely empty channel just re-fetches an empty
 *   page, where trusting the flags would render a lost cache as an empty channel forever.
 * - The rows reach both bounds of the persisted contiguous span. The span's bounds are set from real fetched rows and
 *   every sanctioned prune walks them onto survivors, so an oldest local row newer than the floor (or newest older
 *   than the ceiling) means trusted rows are gone — the shape a dropped persist leaves once the roster-preview
 *   bootstrap re-supplies the feed's newest item. Rows outside the span (deeplink islands below, realtime rows above)
 *   only strengthen the check.
 * - The feed's entire history is local (`loadedFirstPage`), or at least a render window of items survives.
 *
 * A feed the item event log holds through its head (`heldThroughHead`) needs neither flag: this session's walk fetched
 * every item of it from the span up, so it opens from the store however few items it has, and scroll-back pages the
 * rest from the span's floor.
 *
 * A caller that gets `false` for a feed still flagged `loadedLastPage` should clear the flag and fetch, so the
 * healing persists past this session.
 */
export function canSkipInitialTailFetch({
  loadedLastPage,
  loadedFirstPage,
  cache,
  spanFloor,
  spanCeiling,
  windowSize,
  heldThroughHead = false,
}: {
  loadedLastPage: boolean;
  loadedFirstPage: boolean;
  cache: LocalFeedCache;
  spanFloor: string | null;
  spanCeiling: string | null;
  windowSize: number;
  heldThroughHead?: boolean;
}): boolean {
  if (!loadedLastPage && !heldThroughHead) return false;
  if (cache.count === 0) return false;
  if (
    spanFloor &&
    (cache.oldestCreatedAt === null || new Date(cache.oldestCreatedAt).getTime() > new Date(spanFloor).getTime())
  ) {
    return false;
  }
  if (
    spanCeiling &&
    (cache.newestCreatedAt === null || new Date(cache.newestCreatedAt).getTime() < new Date(spanCeiling).getTime())
  ) {
    return false;
  }
  if (loadedFirstPage || heldThroughHead) return true;
  return cache.count >= windowSize;
}

/**
 * Whether a feed that opened from the store should still page back once from its span's floor.
 *
 * A feed the item log holds through its head opens without a request, however few rows it has. With less than a page
 * of them it renders shorter than its container, so no scroll ever asks for older history: one older page fills it.
 *
 * @param pageSize - The rows the view needs to fill itself: a render window, or a thread's page.
 */
export function shouldPageOlderOnOpen({
  heldThroughHead,
  loadedFirstPage,
  count,
  pageSize,
}: {
  heldThroughHead: boolean;
  loadedFirstPage: boolean;
  count: number;
  pageSize: number;
}): boolean {
  return heldThroughHead && !loadedFirstPage && count < pageSize;
}

// Postgres hands timestamptz columns back in its own display format; the API only takes ISO-8601.
//
// The bound is the span's bare timestamp and `until` is inclusive, so a feed holding a full page of
// items that share one `createdAt` cannot page past them — the bound never moves. The event log's
// own cursor would page past them, but a page here starts from the persisted span, not from the
// previous request; until then such a feed stops early rather than looping.
function resolveAnchorTimestamp(currentFeed: Feed | undefined, direction: "newer" | "older", anchor: FeedFetchAnchor) {
  if (anchor !== "contiguity") {
    return undefined;
  }
  const bound = direction === "newer" ? currentFeed?.newestContiguousCreatedAt : currentFeed?.oldestContiguousCreatedAt;
  return bound ? new Date(bound).toISOString() : undefined;
}

function sameInstant(a: string | null | undefined, b: string | null | undefined) {
  if (!a || !b) return a === b;
  return new Date(a).getTime() === new Date(b).getTime();
}

function contiguitySpanUpdates(
  currentFeed: Feed | undefined,
  items: { createdAt?: string | null }[] | undefined,
  anchoring: PageAnchoring,
): Partial<Feed> {
  const merged = mergeContiguousRange(
    { oldest: currentFeed?.oldestContiguousCreatedAt, newest: currentFeed?.newestContiguousCreatedAt },
    items?.map((i) => i.createdAt).filter((createdAt): createdAt is string => !!createdAt) ?? [],
    anchoring,
  );

  if (!merged) {
    return {};
  }
  const updates: Partial<Feed> = {};
  if (!sameInstant(merged.oldest, currentFeed?.oldestContiguousCreatedAt)) {
    updates.oldestContiguousCreatedAt = merged.oldest;
  }
  if (!sameInstant(merged.newest, currentFeed?.newestContiguousCreatedAt)) {
    updates.newestContiguousCreatedAt = merged.newest;
  }
  // A merged floor above the previous ceiling means the span restarted at a disjoint tail page.
  // The restarted span no longer reaches the start of history, so `loadedFirstPage` must fall with
  // it: left set, it both lifts the render clamp over the hole and blocks the older paging that
  // would heal it.
  const previousNewest = currentFeed?.newestContiguousCreatedAt;
  if (
    currentFeed?.loadedFirstPage &&
    previousNewest &&
    new Date(merged.oldest).getTime() > new Date(previousNewest).getTime()
  ) {
    updates.loadedFirstPage = false;
  }
  return updates;
}
